// src/BookSelect.tsx
// Picks one of the user's books, or adds a new one in place ("Add a book…"),
// instead of a free-text book code. Used by the Library's asset panel and the
// project pages. The API refuses a code that isn't in the list, so typos
// can't quietly start a new book (and a new render folder).
import { useState, type KeyboardEvent } from 'react';
import { BOOK_CODE_RE, MAX_BOOK_TITLE, bookLabel } from '@ring/shared/books';
import { useBooks } from './booksApi';
import './BookSelect.css';

const ADD = '\u0000add';

export function BookSelect({ value, onChange, id }: {
  /** The current bookCode, '' for none. */
  value:    string;
  onChange: (bookCode: string) => void;
  id?:      string;
}) {
  const { books, error: loadError, add } = useBooks();
  const [adding, setAdding] = useState(false);
  const [code, setCode]     = useState('');
  const [title, setTitle]   = useState('');
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState<string | null>(null);

  // A code already on this asset or project is always shown, even before the list arrives
  const known = books?.some(b => b.bookCode.toUpperCase() === value.toUpperCase()) ?? false;

  async function submit() {
    if (!BOOK_CODE_RE.test(code)) {
      setError('A code is 1–20 letters, digits, - or _ (e.g. IM1).');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const book = await add({ bookCode: code, title: title.trim() });
      onChange(book.bookCode);
      setAdding(false);
      setCode('');
      setTitle('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Couldn\'t add the book.');
    } finally {
      setBusy(false);
    }
  }

  // This sits inside other forms (the new-project form), so Enter adds the book rather than submitting them
  function onKey(e: KeyboardEvent) {
    if (e.key === 'Enter') { e.preventDefault(); void submit(); }
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setAdding(false); }
  }

  if (adding) {
    return (
      <span className="book-add">
        <span className="book-add-row">
          <input type="text" aria-label="New book code" placeholder="Code, e.g. IM2" maxLength={20} size={9} autoFocus
            value={code} onChange={e => setCode(e.target.value.toUpperCase().replace(/\s/g, ''))} onKeyDown={onKey} />
          <input type="text" aria-label="Title" placeholder="Title (optional)" maxLength={MAX_BOOK_TITLE}
            value={title} onChange={e => setTitle(e.target.value)} onKeyDown={onKey} />
          <button type="button" disabled={busy || !code} onClick={() => void submit()}>Add</button>
          <button type="button" disabled={busy} onClick={() => { setAdding(false); setError(null); }}>Cancel</button>
        </span>
        {error && <span className="book-add-error">{error}</span>}
      </span>
    );
  }

  return (
    <select id={id} value={value} disabled={books === null && !loadError}
      onChange={e => { if (e.target.value === ADD) setAdding(true); else onChange(e.target.value); }}
      title={loadError ?? undefined}>
      <option value="">No book</option>
      {value && !known && <option value={value}>{value}</option>}
      {books?.map(b => <option key={b.bookCode} value={b.bookCode}>{bookLabel(b)}</option>)}
      <option value={ADD}>Add a book…</option>
    </select>
  );
}
